var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useI18n } from '@wordpress/react-i18n';
import { normalizeName, stationsAlongPath, findKAlternativePaths, pathElectrified, buildGraph, findStationNode, pathDistanceKm } from './route-calculator';
import { fetchStations, loadRouteData, calculateRouteMileage } from './api';
import { STATIONS_1520, STATIONS_1435, MAX_ROUTES_COUNT } from './constants';
/** Kai TVS segmente įjungta „Svoris neprivalomas“ – neprivalomas ir brutto, ir neto svoris. */
function isSegmentWeightOptional(selectedSegment, segmentsList) {
    var _a;
    if (!selectedSegment || selectedSegment === '--')
        return false;
    const want = selectedSegment.trim();
    const seg = segmentsList.find(s => (s.segment_label || '').trim() === want);
    if (!seg)
        return false;
    const raw = seg;
    const w = (_a = raw.weight_optional) !== null && _a !== void 0 ? _a : raw.weightOptional;
    if (w === true || w === 'yes' || w === 1 || w === '1')
        return true;
    if (w === false || w === '' || w === 'no' || w === 0 || w === '0')
        return false;
    return (seg.segment_label || '').trim() === 'Tušti konteineriai';
}
function buildAllowedNormToDisplay(gauge) {
    const allowedList = gauge === '1435' ? STATIONS_1435 : STATIONS_1520;
    const map = new Map();
    for (const d of allowedList) {
        map.set(normalizeName(d), d);
    }
    return map;
}
export function useRouteCalculator(props) {
    const { __ } = useI18n();
    const { first_station_label, last_station_label, route_mileage_label, locomotive_weight_label, weight_label, weight_net_label, segment_label, traffic_charge_rate = 0.0013, segments_list = [] } = props;
    const [gauge, setGauge] = useState('1520');
    const [firstStation, setFirstStation] = useState('');
    const [routeVia, setRouteVia] = useState('');
    const [lastStation, setLastStation] = useState('');
    const [routeMileage, setRouteMileage] = useState(null);
    const [locomotiveWeight, setLocomotiveWeight] = useState('');
    const [weight, setWeight] = useState('');
    const [trafficChargeRate] = useState((traffic_charge_rate === null || traffic_charge_rate === void 0 ? void 0 : traffic_charge_rate.toString()) || '0.0013');
    const [selectedSegment, setSelectedSegment] = useState('--');
    const [weightNet, setWeightNet] = useState('');
    const [isCalculatingRoute, setIsCalculatingRoute] = useState(false);
    const [manualRouteMileage, setManualRouteMileage] = useState('');
    const [useManualMileage, setUseManualMileage] = useState(false);
    const [stations, setStations] = useState([]);
    const [isLoadingStations, setIsLoadingStations] = useState(false);
    const [routeData, setRouteData] = useState(null);
    const [isLoadingRouteData, setIsLoadingRouteData] = useState(false);
    const routeDataLoadedRef = useRef(false);
    const [routePath, setRoutePath] = useState(null);
    const [routeCache, setRouteCache] = useState(null);
    const [routeStops, setRouteStops] = useState([]);
    const [alternativeRoutes, setAlternativeRoutes] = useState([]);
    const [isCalculatingAlternatives, setIsCalculatingAlternatives] = useState(false);
    const [selectedRouteIndex, setSelectedRouteIndex] = useState(null);
    const [isManualRouteSelection, setIsManualRouteSelection] = useState(false);
    const [showAlternativeRoutes, setShowAlternativeRoutes] = useState(false);
    const [hasCalculated, setHasCalculated] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    useEffect(() => {
        setHasCalculated(false);
        setValidationErrors([]);
    }, [firstStation, lastStation, routeMileage, manualRouteMileage, useManualMileage, locomotiveWeight, weight, selectedSegment, weightNet]);
    useEffect(() => {
        setIsLoadingStations(true);
        fetchStations(gauge)
            .then(stationList => {
            setStations(stationList);
            setIsLoadingStations(false);
        })
            .catch(error => {
            console.error('Failed to load stations:', error);
            setIsLoadingStations(false);
        });
    }, [gauge]);
    useEffect(() => {
        if (routeDataLoadedRef.current)
            return;
        setIsLoadingRouteData(true);
        loadRouteData()
            .then(data => {
            setRouteData(data);
            routeDataLoadedRef.current = true;
            setIsLoadingRouteData(false);
        })
            .catch(error => {
            console.error('Failed to load route data:', error);
            setIsLoadingRouteData(false);
        });
    }, []);
    const availableStations = useMemo(() => {
        return stations.map(station => ({ label: station, value: station }));
    }, [stations]);
    const viaOptions = useMemo(() => {
        if (!firstStation || !lastStation)
            return [];
        if (selectedRouteIndex !== null && alternativeRoutes[selectedRouteIndex]) {
            const selectedStops = alternativeRoutes[selectedRouteIndex].stops;
            if (selectedStops.length > 2) {
                const intermediateStops = selectedStops.slice(1, -1);
                return intermediateStops.map(station => ({ label: station, value: station }));
            }
            return [];
        }
        if (routeStops.length > 2) {
            const intermediateStops = routeStops.slice(1, -1);
            return intermediateStops.map(station => ({ label: station, value: station }));
        }
        return [];
    }, [firstStation, lastStation, selectedRouteIndex, alternativeRoutes, routeStops]);
    const selectedSegmentCharge = useMemo(() => {
        var _a;
        if (!selectedSegment || selectedSegment === '--' || !segments_list)
            return null;
        const segment = segments_list.find(s => s.segment_label === selectedSegment);
        return (_a = segment === null || segment === void 0 ? void 0 : segment.charge) !== null && _a !== void 0 ? _a : null;
    }, [selectedSegment, segments_list]);
    const selectedStationsCoords = useMemo(() => {
        const coords = [];
        const allowedNormToDisplay = buildAllowedNormToDisplay(gauge);
        const addCoordsFromCache = (cache) => {
            if (firstStation) {
                const normName = normalizeName(firstStation);
                const sp = cache.stationPoints.find(s => s.norm === normName);
                if (sp)
                    coords.push({ name: firstStation, lat: sp.lat, lon: sp.lon });
            }
            if (lastStation) {
                const normName = normalizeName(lastStation);
                const sp = cache.stationPoints.find(s => s.norm === normName);
                if (sp)
                    coords.push({ name: lastStation, lat: sp.lat, lon: sp.lon });
            }
            if (routeVia) {
                const normName = normalizeName(routeVia);
                const sp = cache.stationPoints.find(s => s.norm === normName);
                if (sp)
                    coords.push({ name: routeVia, lat: sp.lat, lon: sp.lon });
            }
        };
        if (routeCache) {
            addCoordsFromCache(routeCache);
        }
        else if (routeData) {
            const tempCache = buildGraph(routeData, gauge);
            addCoordsFromCache(tempCache);
        }
        return coords;
    }, [firstStation, lastStation, routeVia, routeCache, routeData, gauge]);
    const tkb = useMemo(() => {
        const locWeight = parseFloat(locomotiveWeight) || 0;
        const totalWeight = parseFloat(weight) || 0;
        const mileage = routeMileage || 0;
        if (mileage === 0)
            return null;
        return (locWeight + totalWeight) * mileage;
    }, [locomotiveWeight, weight, routeMileage]);
    const trafficChargePrice = useMemo(() => {
        if (tkb === null)
            return null;
        const rate = parseFloat(trafficChargeRate) || 0;
        return tkb * rate;
    }, [tkb, trafficChargeRate]);
    const tkmNet = useMemo(() => {
        const netWeight = parseFloat(weightNet) || 0;
        const mileage = routeMileage || 0;
        if (mileage === 0)
            return null;
        return netWeight * mileage;
    }, [weightNet, routeMileage]);
    const segmentPrice = useMemo(() => {
        if (tkmNet === null || selectedSegmentCharge === null)
            return null;
        return tkmNet * selectedSegmentCharge;
    }, [tkmNet, selectedSegmentCharge]);
    const servicePrice = useMemo(() => {
        const trafficPrice = trafficChargePrice || 0;
        const segPrice = segmentPrice || 0;
        if (trafficPrice === 0 && segPrice === 0)
            return null;
        return trafficPrice + segPrice;
    }, [trafficChargePrice, segmentPrice]);
    const segmentOptions = useMemo(() => {
        const defaultOption = [{ label: '--', value: '--' }];
        if (!segments_list || segments_list.length === 0)
            return defaultOption;
        return [
            ...defaultOption,
            ...segments_list.map(segment => ({
                label: segment.segment_label || '',
                value: segment.segment_label || ''
            }))
        ];
    }, [segments_list]);
    const computeAlternatives = useCallback((basePath, cache, fromStation, toStation, viaStation, gaugeParam) => __awaiter(this, void 0, void 0, function* () {
        if (!cache || !basePath || basePath.length === 0)
            return;
        setIsCalculatingAlternatives(true);
        setAlternativeRoutes([]);
        setSelectedRouteIndex(null);
        const allowedNormToDisplay = buildAllowedNormToDisplay(gaugeParam);
        const fromMatch = findStationNode(cache, fromStation, allowedNormToDisplay);
        const toMatch = findStationNode(cache, toStation, allowedNormToDisplay);
        const fromNode = fromMatch.node;
        const toNode = toMatch.node;
        if (!fromNode || !toNode) {
            setIsCalculatingAlternatives(false);
            return;
        }
        const firstStops = stationsAlongPath(basePath, cache, allowedNormToDisplay);
        if (firstStops.length > 0) {
            firstStops[0] = fromStation;
            firstStops[firstStops.length - 1] = toStation;
        }
        else {
            firstStops.push(fromStation, toStation);
        }
        const baseDistance = pathDistanceKm(basePath, cache.edgeMeta);
        if (baseDistance === null || baseDistance <= 0) {
            setIsCalculatingAlternatives(false);
            return;
        }
        const routes = [{
                stops: firstStops,
                distance: Number(baseDistance.toFixed(2)),
                electrified: pathElectrified(basePath, cache.edgeMeta),
                path: basePath
            }];
        const seenStopsKey = new Map();
        const stopsKey = (stops) => stops.map(normalizeName).join('>');
        seenStopsKey.set(stopsKey(firstStops), 0);
        const searchK = Math.min((MAX_ROUTES_COUNT - 1) * 3, 150);
        const maxIter = Math.min(searchK * 2, 300);
        const paths = findKAlternativePaths(cache, fromNode, toNode, searchK, maxIter);
        for (let i = 1; i < paths.length; i++) {
            const p = paths[i];
            if (!p.path || p.path.length < 2 || p.distKm <= 0)
                continue;
            if (viaStation) {
                const viaMatch = findStationNode(cache, viaStation, allowedNormToDisplay);
                if (viaMatch.node && !p.path.includes(viaMatch.node))
                    continue;
            }
            const stops = stationsAlongPath(p.path, cache, allowedNormToDisplay);
            if (stops.length) {
                stops[0] = fromStation;
                stops[stops.length - 1] = toStation;
            }
            else {
                stops.push(fromStation, toStation);
            }
            const key = stopsKey(stops);
            const existingIdx = seenStopsKey.get(key);
            if (existingIdx === undefined) {
                seenStopsKey.set(key, routes.length);
                routes.push({
                    stops,
                    distance: Number(p.distKm.toFixed(2)),
                    electrified: pathElectrified(p.path, cache.edgeMeta),
                    path: p.path
                });
            }
            else if (p.distKm < routes[existingIdx].distance) {
                routes[existingIdx] = {
                    stops,
                    distance: Number(p.distKm.toFixed(2)),
                    electrified: pathElectrified(p.path, cache.edgeMeta),
                    path: p.path
                };
            }
            if (routes.length >= MAX_ROUTES_COUNT)
                break;
            if (i % 10 === 0) {
                routes.sort((a, b) => a.distance - b.distance);
                const validRoutes = routes.filter(r => r.distance > 0 && r.path && r.path.length >= 2);
                const limitedRoutes = validRoutes.slice(0, MAX_ROUTES_COUNT);
                setAlternativeRoutes([...limitedRoutes]);
                yield new Promise(r => setTimeout(r, 0));
            }
        }
        routes.sort((a, b) => a.distance - b.distance);
        const validRoutes = routes.filter(r => r.distance > 0 && r.path && r.path.length >= 2);
        const limitedRoutes = validRoutes.slice(0, MAX_ROUTES_COUNT);
        setAlternativeRoutes(limitedRoutes);
        setIsCalculatingAlternatives(false);
    }), []);
    useEffect(() => {
        setIsManualRouteSelection(false);
        setSelectedRouteIndex(null);
        if (useManualMileage) {
            const manualValue = parseFloat(manualRouteMileage);
            setRouteMileage(isNaN(manualValue) ? null : manualValue);
            return;
        }
        if (!firstStation || !lastStation) {
            setRouteMileage(null);
            setRoutePath(null);
            setRouteCache(null);
            setRouteStops([]);
            setAlternativeRoutes([]);
            setShowAlternativeRoutes(false);
            return;
        }
        if (!routeData) {
            if (isLoadingRouteData) {
                setIsCalculatingRoute(true);
            }
            else {
                setIsCalculatingRoute(false);
                setRouteMileage(null);
            }
            return;
        }
        setIsCalculatingRoute(true);
        setAlternativeRoutes([]);
        setRouteStops([]);
        setShowAlternativeRoutes(false);
        calculateRouteMileage(firstStation, lastStation, routeVia || undefined, gauge, routeData)
            .then(result => {
            setRouteMileage(result.mileage);
            setRoutePath(result.path);
            setRouteCache(result.cache);
            if (result.path && result.cache) {
                const allowedNormToDisplay = buildAllowedNormToDisplay(gauge);
                const stops = stationsAlongPath(result.path, result.cache, allowedNormToDisplay);
                if (stops.length > 0) {
                    stops[0] = firstStation;
                    stops[stops.length - 1] = lastStation;
                }
                else {
                    stops.push(firstStation, lastStation);
                }
                setRouteStops(stops);
                setSelectedRouteIndex(null);
                setIsManualRouteSelection(false);
                computeAlternatives(result.path, result.cache, firstStation, lastStation, routeVia || undefined, gauge);
            }
            else {
                setRouteStops([]);
                setAlternativeRoutes([]);
                setSelectedRouteIndex(null);
                setIsManualRouteSelection(false);
            }
            setIsCalculatingRoute(false);
        })
            .catch(() => {
            setRouteMileage(null);
            setRoutePath(null);
            setRouteCache(null);
            setRouteStops([]);
            setAlternativeRoutes([]);
            setIsCalculatingRoute(false);
        });
    }, [firstStation, lastStation, routeVia, gauge, useManualMileage, manualRouteMileage, routeData, isLoadingRouteData, computeAlternatives]);
    useEffect(() => {
        if (selectedRouteIndex !== null && alternativeRoutes.length > 0 && alternativeRoutes[selectedRouteIndex] && isManualRouteSelection) {
            const selectedRoute = alternativeRoutes[selectedRouteIndex];
            if (selectedRoute.distance > 0) {
                setRouteMileage(selectedRoute.distance);
                setRoutePath(selectedRoute.path);
                setRouteStops(selectedRoute.stops);
                setUseManualMileage(false);
                if (routeVia && selectedRoute.stops.length > 2) {
                    const intermediateStops = selectedRoute.stops.slice(1, -1);
                    if (!intermediateStops.includes(routeVia)) {
                        setRouteVia('');
                    }
                }
                else if (routeVia && selectedRoute.stops.length <= 2) {
                    setRouteVia('');
                }
            }
        }
    }, [selectedRouteIndex, alternativeRoutes, isManualRouteSelection, routeVia]);
    const handleComputeAlternatives = useCallback(() => {
        if (routePath && routeCache) {
            computeAlternatives(routePath, routeCache, firstStation, lastStation, routeVia || undefined, gauge);
        }
    }, [routePath, routeCache, firstStation, lastStation, routeVia, gauge, computeAlternatives]);
    const handleSelectRoute = useCallback((index) => {
        setIsManualRouteSelection(true);
        setSelectedRouteIndex(index);
    }, []);
    const handleCalculate = useCallback(() => {
        const errors = [];
        if (!(firstStation === null || firstStation === void 0 ? void 0 : firstStation.trim()))
            errors.push(first_station_label || __('Pradinė stotis', 'ltg-widgets'));
        if (!(lastStation === null || lastStation === void 0 ? void 0 : lastStation.trim()))
            errors.push(last_station_label || __('Galutinė stotis', 'ltg-widgets'));
        const effectiveMileage = useManualMileage ? parseFloat(manualRouteMileage) : routeMileage;
        if (!effectiveMileage || effectiveMileage <= 0)
            errors.push(route_mileage_label || __('Maršruto rida', 'ltg-widgets'));
        if (!(locomotiveWeight === null || locomotiveWeight === void 0 ? void 0 : locomotiveWeight.trim()) || isNaN(parseFloat(locomotiveWeight)))
            errors.push(locomotive_weight_label || __('Lokomotyvo svoris', 'ltg-widgets'));
        const weightOptional = isSegmentWeightOptional(selectedSegment, segments_list);
        if (!weightOptional && (!(weight === null || weight === void 0 ? void 0 : weight.trim()) || isNaN(parseFloat(weight)))) {
            errors.push(weight_label || __('Svoris, brutto', 'ltg-widgets'));
        }
        if (!weightOptional &&
            (selectedSegment === null || selectedSegment === void 0 ? void 0 : selectedSegment.trim()) &&
            selectedSegment !== '--' &&
            (!(weightNet === null || weightNet === void 0 ? void 0 : weightNet.trim()) || isNaN(parseFloat(weightNet)))) {
            errors.push(weight_net_label || __('Svoris, neto', 'ltg-widgets'));
        }
        if ((weightNet === null || weightNet === void 0 ? void 0 : weightNet.trim()) && (!(selectedSegment === null || selectedSegment === void 0 ? void 0 : selectedSegment.trim()) || selectedSegment === '--')) {
            errors.push(segment_label || __('Segmentas', 'ltg-widgets'));
        }
        if (errors.length > 0) {
            setValidationErrors(errors);
            setHasCalculated(false);
        }
        else {
            setValidationErrors([]);
            setHasCalculated(true);
        }
    }, [firstStation, lastStation, useManualMileage, manualRouteMileage, routeMileage, locomotiveWeight, weight, selectedSegment, weightNet, segments_list, first_station_label, last_station_label, route_mileage_label, locomotive_weight_label, weight_label, weight_net_label]);
    const hasError = useCallback((label, fallback) => validationErrors.length > 0 && validationErrors.includes(label || fallback), [validationErrors]);
    return {
        gauge,
        setGauge,
        firstStation,
        setFirstStation,
        routeVia,
        setRouteVia,
        lastStation,
        setLastStation,
        locomotiveWeight,
        setLocomotiveWeight,
        weight,
        setWeight,
        trafficChargeRate,
        selectedSegment,
        setSelectedSegment,
        weightNet,
        setWeightNet,
        useManualMileage,
        setUseManualMileage,
        manualRouteMileage,
        setManualRouteMileage,
        routeMileage,
        routePath,
        routeCache,
        routeStops,
        alternativeRoutes,
        showAlternativeRoutes,
        setShowAlternativeRoutes,
        isCalculatingRoute,
        isLoadingRouteData,
        isLoadingStations,
        isCalculatingAlternatives,
        selectedRouteIndex,
        hasCalculated,
        validationErrors,
        availableStations,
        viaOptions,
        selectedStationsCoords,
        selectedSegmentCharge,
        segmentOptions,
        tkb,
        trafficChargePrice,
        tkmNet,
        segmentPrice,
        servicePrice,
        handleComputeAlternatives,
        handleSelectRoute,
        handleCalculate,
        hasError,
        MAX_ROUTES_COUNT
    };
}
